import { useFlowSessionById, useUpdateFlowSession } from "@/api/flow_sessions";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils";
import { FlowSession } from "@cohesive/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { Check, Hand, TriangleAlert } from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";

export type ConfirmationMode = "manual" | "bypass";

interface ConfirmationModeOption {
  value: ConfirmationMode;
  label: string;
  description: string;
}

const OPTIONS: ConfirmationModeOption[] = [
  {
    value: "manual",
    label: "Manual",
    description: "Every command asks for confirmation",
  },
  {
    value: "bypass",
    label: "Bypass permissions",
    description: "Auto-accept all commands this session",
  },
];

interface ConfirmationModeButtonProps {
  /** The flow session this composer is attached to; absent while drafting. */
  flowSessionId?: string;
  /** Draft mode, used only when there is no `flowSessionId` yet. */
  draftMode?: ConfirmationMode;
  /** Set the draft mode. Its presence is what makes a session-less control interactive. */
  onDraftModeChange?: (mode: ConfirmationMode) => void;
  iconClassName?: string;
  triggerButtonClassName?: string;
}

/**
 * Confirmation mode ("permissions") for the session this composer is attached
 * to. The mode is server state on the flow session — the single
 * `tool_user_confirmations_disabled` boolean — not a local preference, so the
 * icon is a read of that field first and a switch second.
 *
 * The control sits in the toolbar's icon cluster from first paint, so the mode
 * is visible on opening a chat rather than only once a gated command has
 * surfaced a confirmation. With no session yet (a draft composer) it renders in
 * the same fixed box, disabled and dimmed, so nothing shifts when the session
 * arrives.
 *
 * A session-less composer that *can* carry the choice forward — the hero
 * composer, which mints its session on send — passes `draftMode` /
 * `onDraftModeChange` instead. Then the control is live against that local
 * draft and writes nothing; its owner applies the mode once the session exists.
 *
 * The neutral hand / caution triangle split mirrors the reasoning-effort
 * selector next door: the trigger carries the current value, and the fly-up
 * menu — same width, radius, row height and selected-row treatment — carries
 * the choice. `lc-warning` is the design system's caution token, the same one
 * SessionBypassNotice uses for the live bypass banner.
 */
export function ConfirmationModeButton({
  flowSessionId,
  draftMode,
  onDraftModeChange,
  iconClassName,
  triggerButtonClassName,
}: ConfirmationModeButtonProps) {
  // Shares the query key used elsewhere, so React Query dedupes — no extra fetch.
  const { data: flowSession } = useFlowSessionById(flowSessionId || "");

  // A session-less composer whose owner hands down `onDraftModeChange` holds
  // the mode locally until the session is minted, so it does have a mode to
  // state — the draft one. Without that handler there is nothing to drive, and
  // the control stays the disabled placeholder it has always been.
  const isDraft = !flowSessionId && !!onDraftModeChange;

  // A third state alongside manual/bypassed: with no session id, or with no
  // session object in hand — still loading, or the fetch failed — the control
  // asserts no mode rather than defaulting to a confident "Manual".
  const isUnknown = !isDraft && (!flowSessionId || !flowSession);
  const bypassed = isDraft
    ? draftMode === "bypass"
    : !!flowSession?.tool_user_confirmations_disabled;

  const [open, setOpen] = useState(false);

  const queryClient = useQueryClient();
  const { mutate: updateFlowSession, isPending: isSaving } =
    useUpdateFlowSession({
      // The shared hook only invalidates on success, so without optimism the
      // icon would not flip until a full write + refetch round trip.
      onMutate: async ({ flowSessionId: id, data }) => {
        await queryClient.cancelQueries({ queryKey: ["flowSession", id] });
        const previous = queryClient.getQueryData<FlowSession>([
          "flowSession",
          id,
        ]);
        queryClient.setQueryData<FlowSession>(["flowSession", id], (old) =>
          old ? { ...old, ...data } : old,
        );
        return { previous };
      },
      onError: (error, { flowSessionId: id }, context) => {
        const { previous } = (context ?? {}) as { previous?: FlowSession };
        if (previous) {
          queryClient.setQueryData(["flowSession", id], previous);
        }
        toast.error(error.message);
      },
    });

  const activeValue: ConfirmationMode = bypassed ? "bypass" : "manual";
  const modeLabel = bypassed ? "Bypass permissions" : "Manual";

  return (
    <div className="z-9 order-2">
      <Popover open={open} onOpenChange={setOpen}>
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                disabled={isUnknown}
                aria-label={
                  isUnknown
                    ? "Confirmation mode"
                    : `Confirmation mode: ${modeLabel}`
                }
                className={cn(
                  "transition-colors",
                  bypassed
                    ? "text-lc-warning hover:text-lc-warning hover:bg-lc-warning/15"
                    : "text-muted-foreground hover:text-foreground",
                  isUnknown && "opacity-40",
                  triggerButtonClassName,
                )}
              >
                {bypassed ? (
                  <TriangleAlert className={iconClassName} />
                ) : (
                  <Hand className={iconClassName} />
                )}
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            {isUnknown
              ? "Confirmation mode"
              : `Confirmation mode: ${modeLabel}`}
          </TooltipContent>
        </Tooltip>

        <PopoverContent
          side="top"
          align="start"
          sideOffset={8}
          className="w-72 p-2 rounded-[14px] border"
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          {OPTIONS.map((option) => (
            <button
              key={option.value}
              disabled={isSaving}
              onClick={() => {
                setOpen(false);
                if (isDraft) {
                  // No session to write against yet — the owner holds the
                  // choice and applies it when the session is minted.
                  onDraftModeChange?.(option.value);
                  return;
                }
                if (!flowSessionId) return;
                const next = option.value === "bypass";
                // The render closure's `bypassed` can lag an optimistic write
                // that is already in flight, so the no-op guard reads the cache
                // instead — otherwise a quick undo is silently dropped.
                const current = !!queryClient.getQueryData<FlowSession>([
                  "flowSession",
                  flowSessionId,
                ])?.tool_user_confirmations_disabled;
                if (next === current) return;
                updateFlowSession({
                  flowSessionId,
                  data: { tool_user_confirmations_disabled: next },
                });
              }}
              className={cn(
                "w-full flex items-center gap-3 min-h-11 px-4 py-2 rounded-lg text-left transition-colors",
                "hover:bg-accent",
                activeValue === option.value && "bg-accent/60",
              )}
            >
              {option.value === "bypass" ? (
                <TriangleAlert className="size-4 shrink-0 text-lc-warning" />
              ) : (
                <Hand className="size-4 shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm">{option.label}</div>
                <div className="text-xs text-muted-foreground leading-snug">
                  {option.description}
                </div>
              </div>
              {activeValue === option.value && (
                <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
              )}
            </button>
          ))}
        </PopoverContent>
      </Popover>
    </div>
  );
}
